import { Howl, Howler } from 'howler'
import ZenGarden from './Audio/ZenGarden.js'

export default class Sounds
{
    constructor(_options)
    {
        // Options
        this.time = _options.time
        this.debug = _options.debug

        // Debug
        if(this.debug)
        {
            this.debugFolder = this.debug.addFolder('sounds')
            // this.debugFolder.open()
        }

        // Set up
        this.items = []

        // Howler, 30 sn hiçbir Howl çalmazsa AudioContext'i uyutuyor → Web Audio ambiyansı da susuyordu
        Howler.autoSuspend = false

        this.setSettings()
        this.setMasterVolume()
        this.setMute()
        this.setEngine()
    }

    setSettings()
    {
        // Tüm sesler tools/audio/synth.py ile sentezlendi (static/sounds/llp). Ad → malzeme:
        // wood (domino), block (harf, küp), blockHeavy (büyük harfler), brick (tuğla), plastic (tuş kapağı),
        // rubber (top), metal, glass, bowlingPin, bowlingBall, carHit, screech (dönüş süpürmesi), uiArea,
        // reveal, carHorn1/2 (klakson), horn (oyuncak korna)
        const files = (_name, _count) => Array.from({ length: _count }, (_value, _index) => `./sounds/llp/${_name}-${_index + 1}.wav`)
        const impact = (_name, _file, _count, _extra = {}) => ({
            name: _name,
            sounds: files(_file, _count),
            minDelta: 100,
            velocityMin: 1,
            velocityMultiplier: 0.75,
            volumeMin: 0.2,
            volumeMax: 0.85,
            rateMin: 0.9,
            rateMax: 1.1,
            ..._extra
        })

        this.settings = [
            { name: 'reveal', sounds: files('chime', 1), minDelta: 100, velocityMin: 0, velocityMultiplier: 1, volumeMin: 0.8, volumeMax: 0.8, rateMin: 1, rateMax: 1 },
            impact('wood', 'wood', 5, { minDelta: 30, velocityMin: 0.5 }),
            impact('block', 'block', 5),
            impact('blockHeavy', 'blockHeavy', 3),
            impact('brick', 'brick', 4, { rateMin: 0.85, rateMax: 1 }),
            impact('plastic', 'plastic', 4),
            impact('rubber', 'rubber', 4, { velocityMin: 0.5, volumeMax: 1 }),
            impact('metal', 'metal', 4, { volumeMax: 0.7 }),
            impact('glass', 'glass', 3, { volumeMax: 0.7 }),
            impact('bowlingPin', 'pin', 3, { minDelta: 0, velocityMultiplier: 0.5, volumeMin: 0.35, volumeMax: 1 }),
            impact('bowlingBall', 'heavyBall', 3, { minDelta: 0, velocityMultiplier: 0.5, volumeMin: 0.35, volumeMax: 1 }),
            impact('carHit', 'carHit', 4, { velocityMin: 2, velocityMultiplier: 0.5, volumeMin: 0.2, volumeMax: 0.9 }),
            { name: 'screech', sounds: files('whoosh', 2), minDelta: 1000, velocityMin: 0, velocityMultiplier: 1, volumeMin: 0.35, volumeMax: 0.35, rateMin: 0.9, rateMax: 1.1 },
            { name: 'uiArea', sounds: files('pop', 2), minDelta: 100, velocityMin: 0, velocityMultiplier: 1, volumeMin: 0.5, volumeMax: 0.5, rateMin: 1, rateMax: 1 },
            { name: 'carHorn1', sounds: files('carHorn', 1), minDelta: 0, velocityMin: 0, velocityMultiplier: 1, volumeMin: 0.55, volumeMax: 0.55, rateMin: 1, rateMax: 1 },
            { name: 'carHorn2', sounds: files('carHorn', 1), minDelta: 0, velocityMin: 0, velocityMultiplier: 1, volumeMin: 0.55, volumeMax: 0.55, rateMin: 1.25, rateMax: 1.25 },
            impact('horn', 'toyHorn', 3, { minDelta: 100, velocityMin: 1, velocityMultiplier: 0.75, volumeMin: 0.3, volumeMax: 0.7 })
        ]

        for(const _settings of this.settings)
        {
            this.add(_settings)
        }
    }

    setMasterVolume()
    {
        // Set up
        this.masterVolume = 0.5
        Howler.volume(this.masterVolume)

        window.requestAnimationFrame(() =>
        {
            Howler.volume(this.masterVolume)
        })

        // Debug
        if(this.debug)
        {
            this.debugFolder.add(this, 'masterVolume').step(0.001).min(0).max(1).onChange(() =>
            {
                Howler.volume(this.masterVolume)
            })
        }
    }

    setMute()
    {
        // Set up
        this.muted = typeof this.debug !== 'undefined'
        this.muteListeners = new Set()
        Howler.mute(this.muted)

        // Menüdeki ses anahtarı ile M tuşu aynı yoldan geçer
        this.setMuted = (_value) =>
        {
            this.muted = _value
            Howler.mute(this.muted)
            for(const _listener of this.muteListeners)
            {
                _listener(this.muted)
            }
        }

        // M Key
        window.addEventListener('keydown', (_event) =>
        {
            if(_event.key === 'm')
            {
                this.setMuted(!this.muted)
            }
        })

        // Tab focus / blur
        document.addEventListener('visibilitychange', () =>
        {
            if(document.hidden)
            {
                Howler.mute(true)
            }
            else
            {
                Howler.mute(this.muted)
            }
        })

        // Debug
        if(this.debug)
        {
            this.debugFolder.add(this, 'muted').listen().onChange(() =>
            {
                Howler.mute(this.muted)
            })
        }
    }

    setEngine()
    {
        // Motor: folio'nun özgün motor döngüsü (sounds/engines/1/low_off.mp3), hızla perdesi ve sesi yükselir.
        // Kullanıcı: özgün sese dönülsün ama bağırmasın → ses aralığı 0.4–1'den 0.14–0.38'e (≈ −8 dB),
        // en yüksek oran 1.4'ten 1.2'ye (yüksek devirdeki tiz çığlık azalır).
        // Car.js her kare engine.speed / engine.acceleration yazar; açılış engine.volume.master'ı 0 → 0.7 tween'ler.
        // Zen bahçesi müziği (Audio/ZenGarden.js) ayrı müzik kanalından Howler.masterGain'e gider.
        this.engine = {}
        this.engine.progress = 0
        this.engine.progressEasingUp = 0.3
        this.engine.progressEasingDown = 0.15
        this.engine.speed = 0
        this.engine.speedMultiplier = 2.5
        this.engine.acceleration = 0
        this.engine.accelerationMultiplier = 0.4
        this.engine.rate = { min: 0.4, max: 1.2 }
        this.engine.volume = { min: 0.14, max: 0.38, master: 0 }
        this.engine.sound = new Howl({
            src: ['./sounds/engines/1/low_off.mp3'],
            loop: true,
            volume: 0
        })
        this.engine.sound.play()

        // Müzik / çarpma sesleri anahtarları (sağ üst köşe, SoundToggles.js) — tercih bu tarayıcıda saklanır
        const stored = (_key) =>
        {
            try
            {
                return window.localStorage.getItem(_key) !== '0'
            }
            catch(_error)
            {
                return true
            }
        }
        this.musicEnabled = stored('llpause.music')
        this.sfxEnabled = stored('llpause.sfx')
        this.toggleListeners = new Set()
        const save = (_key, _value) =>
        {
            try
            {
                window.localStorage.setItem(_key, _value ? '1' : '0')
            }
            catch(_error) {}
            for(const _listener of this.toggleListeners)
            {
                _listener()
            }
        }
        this.setMusicEnabled = (_value) =>
        {
            this.musicEnabled = _value
            save('llpause.music', _value)
        }
        this.setSfxEnabled = (_value) =>
        {
            this.sfxEnabled = _value
            save('llpause.sfx', _value)
        }

        const context = Howler.ctx
        if(!context)
        {
            return
        }

        this.music = {}
        this.music.bus = context.createGain()
        this.music.bus.gain.value = 0
        this.music.bus.connect(Howler.masterGain)
        this.music.zen = new ZenGarden(context, this.music.bus)

        // Olaylar ses saatiyle 1.5 sn ileriye planlanır; sekme gizliyken / müzik kapalıyken plan durur
        let paused = true
        window.setInterval(() =>
        {
            const playing = this.musicEnabled && !document.hidden && context.state === 'running' && this.engine.volume.master > 0
            if(!playing)
            {
                paused = true
                return
            }
            if(paused)
            {
                this.music.zen.catchUp(context.currentTime)
                paused = false
            }
            this.music.zen.schedule(context.currentTime + 1.5)
        }, 500)

        // Time tick: hız + gaz → motor; açılış düzeyi → müzik
        this.time.on('tick', () =>
        {
            let progress = Math.abs(this.engine.speed) * this.engine.speedMultiplier + Math.max(this.engine.acceleration, 0) * this.engine.accelerationMultiplier
            progress = Math.min(Math.max(progress, 0), 1)
            this.engine.progress += (progress - this.engine.progress) * this.engine[progress > this.engine.progress ? 'progressEasingUp' : 'progressEasingDown']

            // Motor (özgün): oran + ses
            this.engine.sound.rate(this.engine.rate.min + (this.engine.rate.max - this.engine.rate.min) * this.engine.progress)
            this.engine.sound.volume((this.engine.volume.min + (this.engine.volume.max - this.engine.volume.min) * this.engine.progress) * this.engine.volume.master)

            // Müzik: açılış düzeyi × anahtar
            this.music.bus.gain.setTargetAtTime(this.musicEnabled ? this.engine.volume.master / 0.7 : 0, context.currentTime, 0.4)
        })
    }

    add(_options)
    {
        const item = {
            name: _options.name,
            minDelta: _options.minDelta,
            velocityMin: _options.velocityMin,
            velocityMultiplier: _options.velocityMultiplier,
            volumeMin: _options.volumeMin,
            volumeMax: _options.volumeMax,
            rateMin: _options.rateMin,
            rateMax: _options.rateMax,
            lastTime: 0,
            sounds: []
        }

        for(const _sound of _options.sounds)
        {
            const sound = new Howl({ src: [_sound] })

            item.sounds.push(sound)
        }

        this.items.push(item)
    }

    play(_name, _velocity)
    {
        const item = this.items.find((_item) => _item.name === _name)
        const time = Date.now()
        const velocity = typeof _velocity === 'undefined' ? 0 : _velocity

        // Çarpma sesleri kapalıysa (sağ üst anahtar) çarpma, korna ve efektler çalmaz
        if(!this.sfxEnabled)
        {
            return
        }

        if(item && time > item.lastTime + item.minDelta && (item.velocityMin === 0 || velocity > item.velocityMin))
        {
            // Find random sound
            const sound = item.sounds[Math.floor(Math.random() * item.sounds.length)]

            // Update volume
            let volume = Math.min(Math.max((velocity - item.velocityMin) * item.velocityMultiplier, item.volumeMin), item.volumeMax)
            volume = Math.pow(volume, 2) * 0.8 // kullanıcı: çarpma sesleri %20 kısık
            sound.volume(volume)

            // Update rate
            const rateAmplitude = item.rateMax - item.rateMin
            sound.rate(item.rateMin + Math.random() * rateAmplitude)

            // Play
            sound.play()

            // Save last play time
            item.lastTime = time
        }
    }
}
